import {
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { useState } from 'react';
import { CanvasBar } from '../../../layout/Canvas';
import { Page } from '../../../layout/Page';
import { Section } from '../../../layout/Section';
import { AlertBox } from '../../../components/AlertBox';
import { ButtonWithPromise } from '../../../components/ButtonWithPromise';
import { callFirebaseFunction } from '../../../services/firebaseFunctions';
import { useSnackbar } from '../../../hooks/useSnackbar';

/**
 * Superadmin "Instance Repair" ops tools. Each tool is a superadmin-only onCall in
 * functions/experiences; every one supports dryRun. The SafeExecuteButton's "Dry run"
 * sends dryRun:true, "Execute" sends dryRun:false. ALWAYS dry-run first and read the
 * change-set (esp. `warnings` / `irreversible`) before executing.
 */

type ItemType = 'TASK' | 'MSG' | 'AUTOMATION';
type ToolKey =
  | 'forceMaterialize'
  | 'completeTask'
  | 'forceFixBranching'
  | 'resetItem'
  | 'createMissingSchedules';

const errMessage = (e: unknown): string =>
  (e as { message?: string })?.message || String(e);

const monospaceBox: React.CSSProperties = {
  width: '100%',
  minHeight: 200,
  fontFamily: 'monospace',
  fontSize: 12,
  whiteSpace: 'pre',
  overflow: 'auto',
  marginTop: 12
};

const TOOLS: Record<ToolKey, { fn: string; label: string; blurb: string }> = {
  forceMaterialize: {
    fn: 'experiences_superadmin_forceTriggerItem',
    label: 'Force-materialize item',
    blurb:
      'Push a template TASK/MSG/AUTOMATION onto this instance + create its schedule. instanceId and timelineItemId each accept a single id OR a comma-separated list (acts on every item × every instance). With "overwrite" on, an item that ALREADY exists is re-synced from the template (keeps progress; reopens a COMPLETE task if a new mandatory subtask appears; timing untouched) instead of materialized.'
  },
  completeTask: {
    fn: 'experiences_opsCompleteTask',
    label: 'Complete task',
    blurb:
      'Complete a task. With effects = real pipeline (profile writes, triggers, reconcile). Without effects = just mark done, nothing propagates. timelineItemId = the instance TASK id.'
  },
  forceFixBranching: {
    fn: 'experiences_opsForceFixBranching',
    label: 'Force-fix branching',
    blurb:
      'Close one task (skip or remove) + start another item. timelineItemId = the TASK to close; startItem = the template item to start.'
  },
  resetItem: {
    fn: 'experiences_opsResetTimelineItem',
    label: 'Reset timeline item',
    blurb:
      'Reopen/skip a completed task, remove the items it triggered + retire their schedules. Profile writes are NOT auto-reverted (see warnings). timelineItemId = the instance TASK id.'
  },
  createMissingSchedules: {
    fn: 'experiences_resetupMissedTriggers',
    label: 'Create missing schedules',
    blurb:
      'Create schedules for materialized-but-unscheduled past-due items on this instance. Uses instanceId only.'
  }
};

export const PageInstanceRepair = () => {
  const [tool, setTool] = useState<ToolKey>('forceMaterialize');

  // shared IDs
  const [orgId, setOrgId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [instanceId, setInstanceId] = useState('');
  const [timelineItemId, setTimelineItemId] = useState('');

  // per-tool args
  const [itemType, setItemType] = useState<ItemType>('TASK');
  const [overwrite, setOverwrite] = useState(false);
  const [applyEffects, setApplyEffects] = useState(false);
  const [closeMode, setCloseMode] = useState<'skip' | 'remove'>('skip');
  const [startItemType, setStartItemType] = useState<ItemType>('TASK');
  const [startItemId, setStartItemId] = useState('');
  const [mode, setMode] = useState<'reopen' | 'skip'>('reopen');

  // Execute defaults to OFF → the default action is a safe DRY RUN. The operator must
  // deliberately tick this to mutate the instance for real.
  const [execute, setExecute] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<unknown>(null);
  const { enqueueSnackbar } = useSnackbar();

  const buildPayload = (dryRun: boolean): Record<string, unknown> => {
    const o = orgId.trim();
    const t = templateId.trim();
    const i = instanceId.trim();
    const item = timelineItemId.trim();
    switch (tool) {
      case 'forceMaterialize':
        return { orgId: o, tplId: t, instanceId: i, itemType, itemTemplateId: item, overwrite, dryRun };
      case 'completeTask':
        return { orgId: o, templateId: t, instanceId: i, timelineItemId: item, applyEffects, dryRun };
      case 'forceFixBranching':
        return {
          orgId: o,
          templateId: t,
          instanceId: i,
          timelineItemId: item,
          closeMode,
          startItemType,
          startItemId: startItemId.trim(),
          dryRun
        };
      case 'resetItem':
        return { orgId: o, templateId: t, instanceId: i, timelineItemId: item, mode, dryRun };
      case 'createMissingSchedules':
        return { instanceId: i, dryRun };
    }
  };

  // Minimum required fields per tool (Execute/Dry-run stay disabled until met).
  const ready = (): boolean => {
    if (tool === 'createMissingSchedules') return !!instanceId.trim();
    const base = orgId.trim() && templateId.trim() && instanceId.trim() && timelineItemId.trim();
    if (!base) return false;
    if (tool === 'forceFixBranching') return !!startItemId.trim();
    return true;
  };

  const run = () => {
    setError('');
    setResult(null);
    const dryRun = !execute;
    const kind = dryRun ? 'Dry run' : 'Execute';
    return callFirebaseFunction(TOOLS[tool].fn, buildPayload(dryRun))
      .then((r) => {
        setResult(r);
        enqueueSnackbar(`${kind} successful!`, { variant: 'success' });
        return r;
      })
      .catch((e) => {
        setError(errMessage(e));
        enqueueSnackbar(`${kind} failed!`, { variant: 'error' });
        throw e;
      });
  };

  return (
    <Page>
      <Section>
        <AlertBox color="red">
          ⚠ These tools mutate <b>production</b> experience instances directly (superadmin-only).
          Always <b>Dry run</b> first and review the change-set — especially <code>warnings</code> and{' '}
          <code>irreversible</code> — before <b>Execute</b>. Sent messages and external automation
          effects cannot be undone.
        </AlertBox>
      </Section>

      <Section>
        <CanvasBar>Instance Repair</CanvasBar>
        <Card>
          <CardContent>
            <TextField
              select
              label="Tool"
              value={tool}
              onChange={(e) => {
                setTool(e.target.value as ToolKey);
                setResult(null);
                setError('');
              }}
              fullWidth
              margin="dense"
            >
              {(Object.keys(TOOLS) as ToolKey[]).map((k) => (
                <MenuItem key={k} value={k}>
                  {TOOLS[k].label}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              {TOOLS[tool].blurb}
            </Typography>

            <TextField
              label="orgId"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              fullWidth
              margin="dense"
              disabled={tool === 'createMissingSchedules'}
            />
            <TextField
              label="experienceTemplateId"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              fullWidth
              margin="dense"
              disabled={tool === 'createMissingSchedules'}
            />
            <TextField
              label={
                tool === 'forceMaterialize'
                  ? 'experienceInstanceId(s) — one or comma-separated'
                  : 'experienceInstanceId'
              }
              value={instanceId}
              onChange={(e) => setInstanceId(e.target.value)}
              fullWidth
              margin="dense"
            />
            <TextField
              label={tool === 'forceMaterialize' ? 'timelineItemId(s) — template item id(s), one or comma-separated' : 'timelineItemId (instance task id)'}
              value={timelineItemId}
              onChange={(e) => setTimelineItemId(e.target.value)}
              fullWidth
              margin="dense"
              disabled={tool === 'createMissingSchedules'}
            />

            {/* per-tool args */}
            {tool === 'forceMaterialize' && (
              <>
                <TextField
                  select
                  label="itemType"
                  value={itemType}
                  onChange={(e) => setItemType(e.target.value as ItemType)}
                  fullWidth
                  margin="dense"
                >
                  {['TASK', 'MSG', 'AUTOMATION'].map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={overwrite}
                      onChange={(e) => setOverwrite(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="overwrite (re-sync an existing item from the template, keep progress — TASK only)"
                />
              </>
            )}

            {tool === 'completeTask' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyEffects}
                    onChange={(e) => setApplyEffects(e.target.checked)}
                    color="primary"
                  />
                }
                label="applyEffects (fire profile writes / triggers / reconcile)"
              />
            )}

            {tool === 'forceFixBranching' && (
              <>
                <TextField
                  select
                  label="closeMode"
                  value={closeMode}
                  onChange={(e) => setCloseMode(e.target.value as 'skip' | 'remove')}
                  fullWidth
                  margin="dense"
                >
                  <MenuItem value="skip">skip</MenuItem>
                  <MenuItem value="remove">remove</MenuItem>
                </TextField>
                <TextField
                  select
                  label="startItemType"
                  value={startItemType}
                  onChange={(e) => setStartItemType(e.target.value as ItemType)}
                  fullWidth
                  margin="dense"
                >
                  {['TASK', 'MSG', 'AUTOMATION'].map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="startItemId (template item id to start)"
                  value={startItemId}
                  onChange={(e) => setStartItemId(e.target.value)}
                  fullWidth
                  margin="dense"
                />
              </>
            )}

            {tool === 'resetItem' && (
              <TextField
                select
                label="mode"
                value={mode}
                onChange={(e) => setMode(e.target.value as 'reopen' | 'skip')}
                fullWidth
                margin="dense"
              >
                <MenuItem value="reopen">reopen (back to NOT_STARTED)</MenuItem>
                <MenuItem value="skip">skip (mark SKIPPED)</MenuItem>
              </TextField>
            )}

            <div style={{ marginTop: 16 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={execute}
                    onChange={(e) => setExecute(e.target.checked)}
                    color="secondary"
                  />
                }
                label={
                  execute
                    ? '⚠ Execute for real — this WILL mutate the instance'
                    : 'Execute for real (leave unchecked to dry-run)'
                }
              />
              <div style={{ marginTop: 8 }}>
                <ButtonWithPromise
                  variant="contained"
                  color={execute ? 'secondary' : 'primary'}
                  disabled={!ready()}
                  pending={execute ? 'Executing…' : 'Dry running…'}
                  onClick={run}
                >
                  {execute ? `Execute ${TOOLS[tool].label} (LIVE)` : `Dry run ${TOOLS[tool].label}`}
                </ButtonWithPromise>
              </div>
            </div>

            {error && (
              <Typography color="error" style={{ marginTop: 12 }}>
                {error}
              </Typography>
            )}

            {!!result && (
              <textarea readOnly value={JSON.stringify(result, null, 2)} style={monospaceBox} />
            )}
          </CardContent>
        </Card>
      </Section>
    </Page>
  );
};
